// src/components/scheduling/DatePicker.jsx
import React, { useState } from 'react';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  X, 
  AlertTriangle,
  CheckCircle,
  Info
} from 'lucide-react';

const DatePicker = ({ 
  selectedDates = [], 
  onDatesChange, 
  timeSlot = { start: '10:00', duration: 60 },
  onTimeSlotChange,
  conflictDates = [],
  existingSessions = [],
  className = ''
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [conflictModal, setConflictModal] = useState(null);


  // Calendar navigation
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  // Get calendar dates for the current month
  const getCalendarDates = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay()); // Start from Sunday
    
    const dates = [];
    const current = new Date(startDate);
    
    // Get 6 weeks worth of dates (42 days)
    for (let i = 0; i < 42; i++) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return dates;
  };

  // Check if a date is selected
  const isDateSelected = (date) => {
    // Use local date formatting to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const result = selectedDates.some(selected => 
      selected.date === dateStr || selected === dateStr
    );
    
    
    return result;
  };

  // Check if a date has conflicts
  const isDateConflicted = (date) => {
    // Use local date formatting to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return conflictDates.includes(dateStr);
  };

  // Check if date is in the past
  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Get existing sessions for a specific date
  const getExistingSessionsForDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    return existingSessions.filter(session => session.date === dateStr);
  };

  // Check if a date has existing sessions
  const hasExistingSessions = (date) => {
    return getExistingSessionsForDate(date).length > 0;
  };

  // Handle date click
  const handleDateClick = (date) => {
    if (isPastDate(date)) return;

    // Use local date formatting to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    if (isDateSelected(date)) {
      // Remove date
      const newDates = selectedDates.filter(selected => {
        const selectedDateStr = typeof selected === 'string' ? selected : selected.date;
        return selectedDateStr !== dateStr;
      });
      onDatesChange(newDates);
    } else {
      // Check for conflicts before adding the date
      const existingSessionsOnDate = getExistingSessionsForDate(date);
      const hasConflict = existingSessionsOnDate.some(session => {
        // Check if the new time slot overlaps with any existing session
        const newStart = timeSlot.start;
        const newDuration = timeSlot.duration;
        const existingStart = session.startTime;
        const existingDuration = session.duration;
        
        return hasTimeOverlap(newStart, newDuration, existingStart, existingDuration);
      });

      if (hasConflict) {
        // Show conflict modal and don't add the date
        const conflictSession = existingSessionsOnDate.find(session => 
          hasTimeOverlap(timeSlot.start, timeSlot.duration, session.startTime, session.duration)
        );
        
        setConflictModal({
          yourTime: `${formatTimeForAlert(timeSlot.start)} - ${formatTimeForAlert(addMinutesToTime(timeSlot.start, timeSlot.duration))}`,
          conflictClass: conflictSession.classTitle,
          conflictTime: `${formatTimeForAlert(conflictSession.startTime)} - ${formatTimeForAlert(addMinutesToTime(conflictSession.startTime, conflictSession.duration))}`
        });
        return;
      }

      // Add date with current time slot (no conflicts)
      const newDate = {
        date: dateStr,
        start_time: timeSlot.start,
        duration: timeSlot.duration
      };
      onDatesChange([...selectedDates, newDate]);
    }
  };

  // Helper function to check if two time slots overlap
  const hasTimeOverlap = (start1, duration1, start2, duration2) => {
    const timeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const start1Minutes = timeToMinutes(start1);
    const end1Minutes = start1Minutes + duration1;
    const start2Minutes = timeToMinutes(start2);
    const end2Minutes = start2Minutes + duration2;

    return start1Minutes < end2Minutes && start2Minutes < end1Minutes;
  };

  // Helper function to format time for alert messages
  const formatTimeForAlert = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Helper function to add minutes to a time string
  const addMinutesToTime = (timeStr, minutesToAdd) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + minutesToAdd;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;
    return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
  };

  // Remove a specific date
  const removeDate = (dateToRemove) => {
    const newDates = selectedDates.filter(selected => {
      const selectedDateStr = typeof selected === 'string' ? selected : selected.date;
      const removeDateStr = typeof dateToRemove === 'string' ? dateToRemove : dateToRemove.date;
      return selectedDateStr !== removeDateStr;
    });
    onDatesChange(newDates);
  };

  // Update time for all selected dates
  const updateAllTimes = (newTime, newDuration) => {
    const updatedDates = selectedDates.map(selected => ({
      date: typeof selected === 'string' ? selected : selected.date,
      start_time: newTime,
      duration: newDuration
    }));
    onDatesChange(updatedDates);
    onTimeSlotChange({ start: newTime, duration: newDuration });
  };

  const calendarDates = getCalendarDates();
  const monthYear = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Time Slot Configuration */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-3 flex items-center">
          <Clock className="w-5 h-5 mr-2 text-[#7A99D8] dark:text-[#8FAEE6]" />
          Default Time Slot
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-1">
              Start Time
            </label>
            <input
              type="time"
              value={timeSlot.start}
              onChange={(e) => updateAllTimes(e.target.value, timeSlot.duration)}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-1">
              Duration (minutes)
            </label>
            <input
              type="number"
              min="15"
              max="480"
              step="15"
              value={timeSlot.duration || ''}
              placeholder="e.g., 60"
              onChange={(e) => {
                const value = e.target.value;
                const duration = value === '' ? '' : parseInt(value) || '';
                updateAllTimes(timeSlot.start, duration);
              }}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Calendar Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 flex items-center">
            <CalendarIcon className="w-5 h-5 mr-2 text-[#7A99D8] dark:text-[#8FAEE6]" />
            {monthYear}
          </h3>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={goToPreviousMonth}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-[#7A99D8] dark:hover:text-[#8FAEE6] hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              ←
            </button>
            <button
              type="button"
              onClick={goToToday}
              className="px-3 py-1 text-sm text-[#7A99D8] dark:text-[#8FAEE6] border border-[#7A99D8] dark:border-[#8FAEE6] hover:bg-[#7A99D8] dark:hover:bg-[#8FAEE6] hover:text-white dark:hover:text-gray-900 transition-colors rounded"
            >
              Today
            </button>
            <button
              type="button"
              onClick={goToNextMonth}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-[#7A99D8] dark:hover:text-[#8FAEE6] hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              →
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="p-4">
          {/* Days of week header */}
          <div className="grid grid-cols-7 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-2">
            {daysOfWeek.map(day => (
              <div key={day} className="p-3 text-center text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 border-r border-gray-200 dark:border-gray-700 last:border-r-0">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar dates */}
          <div className="grid grid-cols-7 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            {calendarDates.map((date, index) => {
              const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
              const isToday = date.toDateString() === new Date().toDateString();
              const isSelected = isDateSelected(date);
              const isConflicted = isDateConflicted(date);
              const isPast = isPastDate(date);
              const existingSessionsOnDate = getExistingSessionsForDate(date);
              const hasExisting = existingSessionsOnDate.length > 0;

              return (
                <div key={index} className="relative border-r border-b border-gray-200 dark:border-gray-700 last:border-r-0">
                  <button
                    type="button"
                    onClick={() => handleDateClick(date)}
                    disabled={isPast}
                    className={`
                      w-full p-3 text-sm transition-colors relative min-h-[80px] flex flex-col items-center justify-start
                      ${!isCurrentMonth ? 'text-gray-300 dark:text-gray-600 bg-gray-50 dark:bg-gray-900/50' : 'text-[#3C3C3C] dark:text-gray-200 bg-white dark:bg-gray-800'}
                      ${isToday ? 'ring-2 ring-[#7A99D8] dark:ring-[#8FAEE6] ring-inset' : ''}
                      ${isSelected ? 'bg-[#B7F1B6] dark:bg-green-900/50 text-[#3C3C3C] dark:text-gray-100 font-medium' : ''}
                      ${isConflicted ? 'bg-[#FFC6C5] dark:bg-red-900/50 text-[#3C3C3C] dark:text-gray-100' : ''}
                      ${isPast ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer'}
                      ${!isSelected && !isConflicted && !isPast && isCurrentMonth ? 'hover:bg-gray-50 dark:hover:bg-gray-700/50' : ''}
                    `}
                  >
                    <span className="font-medium">{date.getDate()}</span>
                    
                    {/* Existing sessions indicators */}
                    {hasExisting && isCurrentMonth && (
                      <div className="mt-2 space-y-1 w-full">
                        {existingSessionsOnDate.slice(0, 4).map((session, sessionIndex) => (
                          <div 
                            key={sessionIndex} 
                            className="group relative"
                            title={`${session.classTitle}: ${session.displayText.split(': ')[1]}`}
                          >
                            <div className="flex items-center justify-center px-1">
                              <div className="w-1.5 h-1.5 bg-[#7A99D8] dark:bg-[#8FAEE6] rounded-full mr-1.5 flex-shrink-0"></div>
                              <span className="text-[10px] text-[#7A99D8] dark:text-[#8FAEE6] font-normal leading-tight text-center">
                                {session.timeDisplay}
                              </span>
                            </div>
                            
                            {/* Hover tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 dark:bg-gray-900 text-white dark:text-gray-100 text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 shadow-lg">
                              {session.classTitle}
                              <br />
                              {session.displayText.split(': ')[1]}
                            </div>
                          </div>
                        ))}
                        {existingSessionsOnDate.length > 4 && (
                          <div className="text-[10px] text-[#7A99D8] dark:text-[#8FAEE6] text-center font-medium">
                            +{existingSessionsOnDate.length - 4} more
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Icons */}
                    {isConflicted && (
                      <AlertTriangle className="w-3 h-3 absolute top-0 right-0 text-red-500 dark:text-red-400" />
                    )}
                    {isSelected && (
                      <CheckCircle className="w-3 h-3 absolute top-0 right-0 text-green-600 dark:text-green-400" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Selected Dates Summary */}
      {selectedDates.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-3 flex items-center">
            <CheckCircle className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
            Selected Dates ({selectedDates.length})
          </h3>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {selectedDates.map((dateObj, index) => {
              const date = typeof dateObj === 'string' ? dateObj : dateObj.date;
              const time = typeof dateObj === 'string' ? timeSlot.start : dateObj.start_time;
              
              // Fix timezone issue by parsing date parts directly
              const [year, month, day] = date.split('-').map(Number);
              const dateFormatted = new Date(year, month - 1, day).toLocaleDateString('en-US', { 
                weekday: 'short', 
                month: 'short', 
                day: 'numeric' 
              });

              // Convert time to 12-hour format
              const formatTime = (timeStr) => {
                const [hours, minutes] = timeStr.split(':');
                const hour = parseInt(hours);
                const ampm = hour >= 12 ? 'PM' : 'AM';
                const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                return `${displayHour}:${minutes} ${ampm}`;
              };

              return (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <span className="text-sm text-[#3C3C3C] dark:text-gray-200">
                    {dateFormatted} at {formatTime(time)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeDate(dateObj)}
                    className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Conflicts Warning */}
      {conflictDates.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-500 dark:text-red-400 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Scheduling Conflicts Detected</h3>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                Some dates conflict with existing classes. Please choose different dates or times.
              </p>
            </div>
          </div>
        </div>
      )}


      {/* Conflict Modal */}
      {conflictModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-500 dark:text-red-400 mr-3" />
                <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100">Time Conflict Detected</h3>
              </div>
              
              <div className="space-y-4 mb-6">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Your selected time:</p>
                  <p className="text-sm font-medium text-[#3C3C3C] dark:text-gray-200 bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    {conflictModal.yourTime}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Conflicts with:</p>
                  <p className="text-sm font-medium text-[#3C3C3C] dark:text-gray-200 bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-200 dark:border-red-800">
                    "{conflictModal.conflictClass}" ({conflictModal.conflictTime})
                  </p>
                </div>
                
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Please choose a different time or date to avoid this conflict.
                </p>
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={() => setConflictModal(null)}
                  className="px-4 py-2 bg-[#7A99D8] dark:bg-[#8FAEE6] hover:bg-[#6B8BC9] dark:hover:bg-[#7A99D8] text-white dark:text-gray-900 rounded-lg font-medium transition-colors"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;